import { number, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type { TSignal } from '@repo/shared/schema/report';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/sheet';
import { BanIcon, ChevronRightIcon, GlobeIcon, ScaleIcon, UsersIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { trpc } from '@/providers/trpc';
import { SignalDetailCard } from './signal-detail-card';

const groups = [
  {
    type: 'SANCTIONS',
    get label() {
      return t('landing.globalWatchLists');
    },
    Icon: BanIcon,
  },
  {
    type: 'PEP_SCREENING',
    get label() {
      return t('landing.pepScreening');
    },
    Icon: UsersIcon,
  },
  {
    type: 'ADVERSE_MEDIA',
    get label() {
      return t('landing.adverseMedia');
    },
    Icon: GlobeIcon,
  },
  {
    type: 'JURISDICTION_RISK',
    get label() {
      return t('landing.jurisdictionRisk');
    },
    Icon: ScaleIcon,
  },
] as const;

export const SignalsSection = ({
  reportId,
  signals,
  memberId,
}: {
  reportId: string;
  signals: TSignal[];
  memberId?: string;
}) => {
  useLocale();

  const [selected, setSelected] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const opener = useRef<HTMLButtonElement | null>(null);
  const { data: report } = trpc.report.getReport.useQuery({ reportId });
  const research = report?.researchCoverage?.find(({ subject }) =>
    memberId ? subject.id === memberId : subject.kind !== 'member',
  );
  const subject = research?.subject;
  const coverage = (category: string) =>
    research?.categories?.find((item) => item.category === category);
  const entries = signals.filter((signal) => signal.type === selected);
  const social = signals.filter(
    (signal) => signal.type === 'SOCIAL' && signal.resolution !== 'DISMISSED',
  );
  const open = (type: string, button: HTMLButtonElement, signalId?: string) => {
    opener.current = button;
    setFocused(signalId ?? null);
    setSelected(type);
  };
  const label =
    selected === 'SOCIAL'
      ? t('landing.socialSignals')
      : groups.find((group) => group.type === selected)?.label;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {groups.map(({ type, label, Icon }) => {
          const findings = signals.filter(
            (signal) => signal.type === type && signal.resolution !== 'DISMISSED',
          );
          const issues = findings.filter((signal) => signal.classification !== 'INFORMATIONAL');
          const assessedIssues = issues.filter(
            (signal) => signal.match === 'matching' || signal.identityConfirmed,
          );
          const uncertainCount = findings.filter(
            (signal) => signal.match === 'uncertain' && !signal.identityConfirmed,
          ).length;
          const isNotScreened = coverage(type)?.status === 'not_screened';
          const complete = ['researched', 'no_findings'].includes(coverage(type)?.status ?? '');
          return (
            <button
              className="raven-signal"
              data-risk={
                assessedIssues.some((signal) => signal.severity === 'HIGH')
                  ? 'HIGH'
                  : assessedIssues.some(
                        (signal) => signal.severity === 'MEDIUM' || signal.severity === undefined,
                      )
                    ? 'MEDIUM'
                    : undefined
              }
              key={type}
              onClick={(event) => open(type, event.currentTarget)}
              type="button"
            >
              <span className="flex justify-between gap-2 text-[#8e8198] text-xs">
                {label}
                <Icon className="size-4 shrink-0" />
              </span>
              <span className="flex gap-3 text-[#303785]">
                <strong className="text-2xl">{number(issues.length)}</strong>
                <span className="text-xs leading-relaxed">
                  {issues[0]?.title ??
                    (isNotScreened
                      ? t('policies.not_screened')
                      : complete
                        ? t('auth.noIssuesDetectedInResearchedSources')
                        : t('auth.notSufficientlyAssessed'))}
                </span>
              </span>
              <span className="raven-muted">
                {t('count.issues', { count: issues.length })}{' '}
                {findings.length > issues.length &&
                  t('count.informational', { count: findings.length - issues.length })}
              </span>
              {uncertainCount > 0 && (
                <span className="text-amber-700 text-xs">
                  {t('reports.identityNeedsReview')}: {number(uncertainCount)}
                </span>
              )}
              <span className="raven-link mt-auto self-end">
                {t('landing.moreDetails')} <ChevronRightIcon className="size-3 rtl:rotate-180" />
              </span>
            </button>
          );
        })}
      </div>
      <div>
        <h3 className="mb-3">{t('landing.socialSignals')}</h3>
        <div className="raven-panel space-y-4">
          <span className="raven-muted">{t('landing.socialSignals')}</span>
          <h3>
            <strong className="me-3 text-2xl">{number(social.length)}</strong>
            {coverage('SOCIAL')?.status === 'not_screened'
              ? t('policies.not_screened')
              : social.length
                ? t('auth.findingsAcrossPublicProfilesBusinessSources')
                : t('auth.noSupportedSocialFindings')}
          </h3>
          <p className="raven-muted">
            {t('auth.verifiedAssociationsDiscrepanciesAndRiskIndicatorsAreDistinguishedBelow')}{' '}
          </p>
          {social.slice(0, 3).map((signal) => (
            <button
              className="block w-full rounded-lg border p-3 text-start"
              key={signal._id}
              onClick={(event) => open('SOCIAL', event.currentTarget, signal._id)}
              type="button"
            >
              <span className="block font-medium text-[#35418c] text-xs">{signal.title}</span>
              <span className="raven-muted">
                {signal.classification === 'INFORMATIONAL' ? t('auth.informational') : ''}
                {sourceLabel(signal)}
                {signal.match === 'uncertain' && !signal.identityConfirmed && (
                  <span className="block text-amber-700">{t('reports.identityNeedsReview')}</span>
                )}
              </span>
            </button>
          ))}
          <div className="flex justify-end">
            <button
              className="raven-link"
              onClick={(event) => open('SOCIAL', event.currentTarget)}
              type="button"
            >
              {t('landing.moreDetails')} <ChevronRightIcon className="size-3 rtl:rotate-180" />
            </button>
          </div>
        </div>
      </div>
      <Sheet
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setSelected(null);
          }
        }}
        open={selected !== null}
      >
        <SheetContent
          className="raven-workspace overflow-y-auto p-5 data-[side=right]:w-full data-[side=right]:sm:max-w-xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            opener.current?.focus();
          }}
          side="right"
        >
          <SheetHeader>
            <SheetTitle>{label}</SheetTitle>
            <SheetDescription>
              {t('auth.reviewEvidenceFor')} {subject?.name ?? t('common.thisSubject')}{' '}
              {t('copy.findingInstructions')}{' '}
            </SheetDescription>
          </SheetHeader>
          <p className="my-4 text-sm">
            {coverage(selected ?? '')?.status === 'not_screened'
              ? t('policies.not_screened')
              : (coverage(selected ?? '')?.explanation ?? t('auth.notSufficientlyAssessed'))}
          </p>
          {!entries.length && (
            <div className="raven-panel">
              {t('auth.noSupportedFindingsAreAvailableInThisCategory')}
            </div>
          )}
          <div className="space-y-4">
            {[...entries]
              .sort((a, b) => Number(b._id === focused) - Number(a._id === focused))
              .map((signal) => (
                <SignalDetailCard
                  key={signal._id}
                  onResolved={() => {}}
                  reportId={reportId}
                  signal={signal}
                />
              ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

const sourceLabel = (signal: TSignal) => {
  try {
    return signal.sourceUrl
      ? new URL(signal.sourceUrl).hostname.replace(/^www\./, '')
      : signal.source;
  } catch {
    return signal.source;
  }
};
